/**
 * Opta controller WebSocket service.
 *
 * Routes opta_status / opta_state / opta_event / opta_command_update messages
 * (published farm-scoped by the backend) into the optaSlice.
 */
import { AppDispatch } from '@/store/index';
import { WebSocketMessage } from './types';
import { WebSocketMessageRouter } from './messageRouter';
import {
  optaStatusUpdate,
  optaEntityStateUpdate,
  optaEventReceived,
  optaCommandUpdate,
  optaCfgAck,
  optaOtaUpdate,
} from '@/store/slices/optaSlice';

export class OptaWebSocketService {
  readonly name = 'opta-service';
  readonly version = '1.0.0';
  readonly dependencies: string[] = [];

  private router: WebSocketMessageRouter | null = null;
  private dispatch: AppDispatch | null = null;

  async initialize(router: WebSocketMessageRouter, dispatch: AppDispatch): Promise<void> {
    this.router = router;
    this.dispatch = dispatch;
    for (const type of this.getMessageTypes()) {
      router.registerHandler({
        messageType: type,
        handler: this.handleMessage.bind(this),
        priority: 10,
        service: this.name,
      });
    }
  }

  async cleanup(): Promise<void> {
    if (this.router) {
      for (const type of this.getMessageTypes()) {
        this.router.unregisterHandler(type, this.name);
      }
    }
    this.router = null;
    this.dispatch = null;
  }

  getMessageTypes(): string[] {
    // `opta_io` (live terminal readings) is declared here but handled by the
    // components that want it (useIoLive listens on the base service). It is
    // listed so the consolidated manager forwards it — that list is derived from
    // exactly this method, and a type missing from it is dropped in SILENCE.
    return ['opta_status', 'opta_state', 'opta_event', 'opta_command_update',
      'opta_cfg_ack', 'opta_io'];
  }

  getHandlerPriority(): number {
    return 10;
  }

  getServiceInfo() {
    return {
      name: this.name,
      description: 'Routes Opta controller status/state/event/command updates into Redux',
      version: this.version,
      author: 'Saqia',
      supportedMessageTypes: this.getMessageTypes(),
      dependencies: this.dependencies,
      priority: this.getHandlerPriority(),
    };
  }

  private handleMessage(message: WebSocketMessage): void {
    if (!this.dispatch) return;
    // The backend sends the fields at the top level of message.data.
    const d = (message.data as Record<string, any>) ?? {};
    const ctrl_uid = d.ctrl_uid as string | undefined;
    if (!ctrl_uid) return;

    switch (message.type) {
      case 'opta_status':
        this.dispatch(optaStatusUpdate({
          ctrl_uid, online: !!d.online, fw_rev: d.fw_rev ?? null, ota_state: d.ota_state ?? null,
          // Observed, not owned: a serial-console technician or the firmware's own
          // OTA can raise it, so it must follow the status rather than our commands.
          maintenance: !!d.maintenance,
          // Detected I/O rides along so the terminal pickers can re-render when
          // the hardware changes (module pulled/restacked, expansion rescan).
          hw_inv: d.hw_inv ?? null,
          // Capability flags decide which features the UI may offer. Keyed on
          // these, never on fw_rev.
          caps: d.caps ?? null,
          // Messages the controller never managed to send.
          dropped: d.dropped ?? null,
        }));
        // OTA progress rides on the status (and on OTA_* events) so the firmware
        // panel can follow an upgrade without polling.
        if (d.ota || d.ota_apply !== undefined) {
          this.dispatch(optaOtaUpdate({
            ctrl_uid, ota_apply: d.ota_apply ?? null, ota: d.ota ?? null,
          }));
        }
        break;
      case 'opta_state':
        this.dispatch(optaEntityStateUpdate({
          ctrl_uid, tipo: d.tipo, entity_id: d.entity_id, state: d.state ?? {},
        }));
        break;
      case 'opta_event':
        this.dispatch(optaEventReceived({
          ctrl_uid, tipo: d.tipo ?? null, entity_id: d.entity_id ?? null,
          sev: d.sev ?? 'info', code: d.code ?? 'UNKNOWN', msg: d.msg ?? null,
          ts: d.ts ?? null, created_at: new Date().toISOString(),
          // Rides on every event; on MAINTENANCE_ENTER/EXIT it is the transition
          // edge, arriving ahead of the controller's next retained status.
          maintenance: typeof d.maintenance === 'boolean' ? d.maintenance : undefined,
        }));
        if (d.ota) {
          this.dispatch(optaOtaUpdate({ ctrl_uid, ota: d.ota }));
        }
        break;
      case 'opta_cfg_ack':
        // The controller's verdict on a cfg: applied? persisted to flash?
        this.dispatch(optaCfgAck({
          ctrl_uid, tipo: d.tipo, entity_id: d.entity_id,
          rev: d.rev ?? 0, stored_rev: d.stored_rev ?? 0,
          result: d.result ?? 'error', persisted: !!d.persisted,
          reason: d.reason ?? null, sync_status: d.sync_status ?? '',
        }));
        break;
      case 'opta_command_update':
        this.dispatch(optaCommandUpdate({
          ctrl_uid, req_id: d.req_id, verb: d.verb, state: d.state,
          result: d.result ?? null, reason: d.reason ?? null,
        }));
        break;
    }
  }
}

export const optaWebSocketService = new OptaWebSocketService();
